import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Search, Filter, ChevronDown, X } from 'lucide-react';

export const CourseFilters = ({
  filters,
  setFilters,
  showFilters,
  setShowFilters,
  activeFiltersCount,
  resetFilters,
  filteredCoursCount,
  // Data for dropdowns
  instructeurs,
  themes,
  groupes,
}) => {
  return (
    <div className="bg-white rounded-lg border border-stone-200 p-4 space-y-4">
      {/* Search bar and filter toggle */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Text search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            placeholder="Rechercher par thème, instructeur, groupe, lieu..."
            value={filters.searchText}
            onChange={(e) => setFilters(prev => ({ ...prev, searchText: e.target.value }))}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        
        {/* Filter toggle button */}
        <Button
          variant={showFilters ? "default" : "outline"}
          onClick={() => setShowFilters(!showFilters)}
          className={showFilters ? "bg-[#2E4A3D]" : ""}
          data-testid="filter-toggle"
        >
          <Filter className="w-4 h-4 mr-2" />
          Filtres
          {activeFiltersCount > 0 && (
            <Badge className="ml-2 bg-white text-[#2E4A3D]">{activeFiltersCount}</Badge>
          )}
          <ChevronDown className={`w-4 h-4 ml-2 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
        </Button>
        
        {/* Reset filters */}
        {activeFiltersCount > 0 && (
          <Button variant="ghost" onClick={resetFilters} className="text-stone-500">
            <X className="w-4 h-4 mr-1" />
            Réinitialiser
          </Button>
        )}
      </div>
      
      {/* Expanded filters */}
      {showFilters && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 pt-4 border-t border-stone-200">
          {/* Instructeur filter */}
          <div className="space-y-1.5">
            <Label className="text-sm text-stone-600">Instructeur</Label>
            <Select
              value={filters.instructeurId}
              onValueChange={(v) => setFilters(prev => ({ ...prev, instructeurId: v === 'all' ? '' : v }))}
            >
              <SelectTrigger data-testid="filter-instructeur">
                <SelectValue placeholder="Tous les instructeurs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les instructeurs</SelectItem>
                {instructeurs
                  .filter(i => i.role_club === 'instructeur_admin' || i.role_club === 'instructeur')
                  .map(i => (
                    <SelectItem key={i.id} value={i.id}>{i.prenom} {i.nom}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Thème filter */}
          <div className="space-y-1.5">
            <Label className="text-sm text-stone-600">Thème</Label>
            <Select
              value={filters.themeId}
              onValueChange={(v) => setFilters(prev => ({ ...prev, themeId: v === 'all' ? '' : v }))}
            >
              <SelectTrigger data-testid="filter-theme">
                <SelectValue placeholder="Tous les thèmes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les thèmes</SelectItem>
                {themes.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.nom || t.nom_complet || t.nom_abrege || 'Sans nom'}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Type d'activité filter */}
          <div className="space-y-1.5">
            <Label className="text-sm text-stone-600">Type d'activité</Label>
            <Select
              value={filters.typeActivite}
              onValueChange={(v) => setFilters(prev => ({ ...prev, typeActivite: v === 'all' ? '' : v }))}
            >
              <SelectTrigger data-testid="filter-type">
                <SelectValue placeholder="Tous les types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="collectif">Cours collectif</SelectItem>
                <SelectItem value="cours_particulier">Cours particulier</SelectItem>
                <SelectItem value="dressage">Dressage</SelectItem>
                <SelectItem value="saut">Saut d'obstacle</SelectItem>
                <SelectItem value="balade">Balade</SelectItem>
                <SelectItem value="stage">Stage</SelectItem>
                <SelectItem value="competition">Compétition</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Groupe filter */}
          <div className="space-y-1.5">
            <Label className="text-sm text-stone-600">Groupe</Label>
            <Select
              value={filters.groupeId}
              onValueChange={(v) => setFilters(prev => ({ ...prev, groupeId: v === 'all' ? '' : v }))}
            >
              <SelectTrigger data-testid="filter-groupe">
                <SelectValue placeholder="Tous les groupes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les groupes</SelectItem>
                {groupes.map(g => (
                  <SelectItem key={g.id} value={g.id}>{g.nom}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Niveau galop filter */}
          <div className="space-y-1.5">
            <Label className="text-sm text-stone-600">Niveau galop</Label>
            <Select
              value={filters.niveauGalop}
              onValueChange={(v) => setFilters(prev => ({ ...prev, niveauGalop: v === 'all' ? '' : v }))}
            >
              <SelectTrigger data-testid="filter-niveau">
                <SelectValue placeholder="Tous les niveaux" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les niveaux</SelectItem>
                <SelectItem value="debutant">Débutant</SelectItem>
                <SelectItem value="galop_1">Galop 1</SelectItem>
                <SelectItem value="galop_2">Galop 2</SelectItem>
                <SelectItem value="galop_3">Galop 3</SelectItem>
                <SelectItem value="galop_4">Galop 4</SelectItem>
                <SelectItem value="galop_5">Galop 5</SelectItem>
                <SelectItem value="galop_6">Galop 6</SelectItem>
                <SelectItem value="galop_7">Galop 7</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Catégorie âge filter */}
          <div className="space-y-1.5">
            <Label className="text-sm text-stone-600">Catégorie d'âge</Label>
            <Select
              value={filters.categorieAge}
              onValueChange={(v) => setFilters(prev => ({ ...prev, categorieAge: v === 'all' ? '' : v }))}
            >
              <SelectTrigger data-testid="filter-age">
                <SelectValue placeholder="Toutes les catégories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes les catégories</SelectItem>
                <SelectItem value="adultes">Cours adultes</SelectItem>
                <SelectItem value="enfants">Cours enfants</SelectItem>
                <SelectItem value="petits">Cours petits</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      )}
      
      {/* Results count */}
      <div className="text-sm text-stone-500">
        {filteredCoursCount} cours trouvé{filteredCoursCount > 1 ? 's' : ''}
        {activeFiltersCount > 0 && ` (${activeFiltersCount} filtre${activeFiltersCount > 1 ? 's' : ''} actif${activeFiltersCount > 1 ? 's' : ''})`}
      </div>
    </div>
  );
};

export default CourseFilters;
